/**
 * HUDFrame Component
 * Sci-fi HUD border frame with reactive viewport sizing
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { getDeviceInfo } from '../../utils/deviceDetection'
import { CircularButton } from './CircularButton'
import { playSound } from '../../utils/soundFX'
import './HUDFrame.css'

const HUD_COLOR = '#ee3a17'

export function HUDFrame({ onLeftButtonClick, onRightButtonClick, isTransitioning, showLeftButton, showRightButton, currentMode, showJoinMission, onArtemisClick, showArtemisButton, isArtemisModeActive, audioMuted, onToggleMute, showMuteButton }) {
  // Get device info for mobile optimization  
  const deviceInfo = getDeviceInfo()
  const isMobile = deviceInfo.isMobile
  
  // Enable flicker effects for all devices (memory leaks fixed)
  const shouldFlicker = isTransitioning
  
  // Cache margin objects to prevent recreation
  const marginPortrait  = useMemo(() => ({ left: 16, right: 16, top: 16, bottom: 16 }), [])
  const marginLandscape = useMemo(() => ({ left: 32, right: 32, top: 32, bottom: 32 }), [])

  const getMargin = useCallback(() => {
    const deviceInfo = getDeviceInfo()
    return deviceInfo.isPortrait ? marginPortrait : marginLandscape
  }, [marginPortrait, marginLandscape])

  const [dimensions, setDimensions] = useState(() => {
    const margin = getMargin()
    return {
      width: window.innerWidth - margin.left - margin.right,
      height: window.innerHeight - margin.top - margin.bottom
    }
  })

  useEffect(() => {
    let resizeTimeout
    const handleResize = () => {
      // Use shorter debounce for iOS/Safari to handle fullscreen toolbar changes
      const debounceTime = isMobile ? 50 : 100
      
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        const margin = getMargin()
        setDimensions({
          width: window.innerWidth - margin.left - margin.right,
          height: window.innerHeight - margin.top - margin.bottom
        })
      }, debounceTime)
    }

    // Immediate resize handler for iOS Safari visual viewport changes
    const handleVisualViewportResize = () => {
      const margin = getMargin()
      setDimensions({
        width: window.innerWidth - margin.left - margin.right,
        height: window.innerHeight - margin.top - margin.bottom
      })
    }

    // Force initial resize on mount for mobile to fix svh calculation
    if (isMobile) {
      setTimeout(handleResize, 100)
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)
    
    // Add visual viewport listener for iOS Safari fullscreen changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize)
      window.visualViewport.addEventListener('scroll', handleVisualViewportResize)
    }
    
    return () => {
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize)
        window.visualViewport.removeEventListener('scroll', handleVisualViewportResize)
      }
    }
  }, [getMargin, isMobile])

  // Determine button labels based on current mode
  const leftButtonLabel = currentMode === 'portfolio' ? 'EXPLORE' : 'STARCADE'
  const rightButtonLabel = currentMode === 'starcade' ? 'EXPLORE' : 'PORTFOLIO'

  // At 4K margins are 32px each side → dimensions.width = 2560 - 64 = 2496
  const buttonGap = dimensions.width >= 2496 ? 38 : 30

  return (
    <>
      <div className={`hud-frame ${shouldFlicker ? 'flickering' : ''}`}>
        <HUDTopBar key={`top-${dimensions.width}`} width={dimensions.width} />
        <HUDBottomBar key={`bottom-${dimensions.width}-${dimensions.height}`} width={dimensions.width} height={dimensions.height} />
        <HUDLeftBar key={`left-${dimensions.height}`} height={dimensions.height} showButton={showLeftButton} showJoinMission={showJoinMission} gap={buttonGap} />
        <HUDRightBar key={`right-${dimensions.width}-${dimensions.height}`} width={dimensions.width} height={dimensions.height} showButton={showRightButton} showJoinMission={showJoinMission} gap={buttonGap} />
        {/* Circular buttons */}
        {showLeftButton && !showJoinMission && (
          <CircularButton
            position="left"
            onClick={() => { playSound(currentMode === 'portfolio' ? 'HideCourse' : 'PlotCourse'); onLeftButtonClick(); }}
            label={leftButtonLabel}
          />
        )}
        {showRightButton && !showJoinMission && (
          <CircularButton
            position="right"
            onClick={() => { playSound(currentMode === 'starcade' ? 'HideCourse' : 'PlotCourse'); onRightButtonClick(); }}
            label={rightButtonLabel}
          />
        )}
      </div>
      {/* Rendered outside .hud-frame to avoid overflow:hidden clipping its fixed position */}
      {showArtemisButton && (
        <ArtemisButton onArtemisClick={onArtemisClick} />
      )}
    </>
  )
}

function HUDTopBar({ width }) {
  const deviceInfo = getDeviceInfo()
  const isPortrait = deviceInfo.isPortrait
  const isMobilePortrait = deviceInfo.isMobile && isPortrait
  const chevronWidth = isPortrait ? 100 : 200
  const barWidth = isPortrait ? 40 : 80
  const barHeight = isPortrait ? 2 : 4
  const rectWidth = isPortrait ? 9 : 75
  const centerBreakStart = width / 2 - chevronWidth
  const centerBreakEnd = width / 2 + chevronWidth

  return (
    <svg className="hud-top-bar" width={width} height="60" viewBox={`0 0 ${width} 60`} preserveAspectRatio="none">
      {/* Left corner circle with crosshair */}
      <g className="hud-corner-circle">
        <circle cx="12" cy="42" r="5" fill="none" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="20" y1="42" x2="24" y2="42" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="0" y1="42" x2="4" y2="42" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="12" y1="30" x2="12" y2="34" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="12" y1="50" x2="12" y2="54" stroke={HUD_COLOR} strokeWidth="1" />
      </g>

      {/* Top horizontal bars */}
      <rect x="12" y="13" width={rectWidth} height="9" fill="none" stroke={HUD_COLOR} strokeWidth="1" />
      <line x1={12 + rectWidth + 10} y1="17" x2={centerBreakStart - 13} y2="17" stroke={HUD_COLOR} strokeWidth="1" />
      
      {/* Diagonal chevron up */}
      <line x1={centerBreakStart - 13} y1="17" x2={centerBreakStart} y2="4" stroke={HUD_COLOR} strokeWidth="1" />
      {/* Diagonal chevron down */}
      <line x1={centerBreakEnd} y1="4" x2={centerBreakEnd + 13} y2="17" stroke={HUD_COLOR} strokeWidth="1" />
      
      {/* Center break with bars */}
      <rect x={width / 2 - chevronWidth} y="4" width={chevronWidth * 2} height="1" fill={HUD_COLOR} />

      {/* Center FILLED bar */}
      <rect x={width / 2 - barWidth / 2} y="8" width={barWidth} height={barHeight} fill={HUD_COLOR} />

      <line x1={centerBreakEnd + 13} y1="17" x2={width - 12 - rectWidth - 10} y2="17" stroke={HUD_COLOR} strokeWidth="1" />
      {!isMobilePortrait && <rect x={width - 12 - rectWidth} y="13" width={rectWidth} height="9" fill="none" stroke={HUD_COLOR} strokeWidth="1" />}

      {/* Left corner circle with crosshair */}
      <g className="hud-corner-circle">
        <circle cx="12" cy="42" r="5" fill="none" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="20" y1="42" x2="24" y2="42" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="0" y1="42" x2="4" y2="42" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="12" y1="30" x2="12" y2="34" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="12" y1="50" x2="12" y2="54" stroke={HUD_COLOR} strokeWidth="1" />
      </g>

      {/* Right corner circle with crosshair */}
      <g className="hud-corner-circle">
        <circle cx={width - 12} cy="42" r="5" fill="none" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1={width - 20} y1="42" x2={width - 24} y2="42" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1={width} y1="42" x2={width - 4} y2="42" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1={width - 12} y1="30" x2={width - 12} y2="34" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1={width - 12} y1="50" x2={width - 12} y2="54" stroke={HUD_COLOR} strokeWidth="1" />
      </g>
    </svg>
  )
}

function HUDBottomBar({ width, height }) {

  const deviceInfo = getDeviceInfo()
  const isPortrait = deviceInfo.isPortrait
  const isMobilePortrait = deviceInfo.isMobile && isPortrait
  const chevronWidth = isPortrait ? 100 : 200
  const barWidth = isPortrait ? 40 : 80
  const barHeight = isPortrait ? 2 : 4
  const rectWidth = isPortrait ? 9 : 75
  const centerBreakStart = width / 2 - chevronWidth
  const centerBreakEnd = width / 2 + chevronWidth

  return (
    <svg className="hud-bottom-bar" width={width} height="60" viewBox={`0 0 ${width} 60`} preserveAspectRatio="none">
      {/* Left corner circle with crosshair */}
      <g className="hud-corner-circle">
        <circle cx="12" cy="18" r="5" fill="none" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="20" y1="18" x2="24" y2="18" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="0" y1="18" x2="4" y2="18" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="12" y1="6" x2="12" y2="10" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="12" y1="26" x2="12" y2="30" stroke={HUD_COLOR} strokeWidth="1" />
      </g>

      {/* Bottom horizontal bars */}
      <rect x="12" y="38" width={rectWidth} height="9" fill="none" stroke={HUD_COLOR} strokeWidth="1" />
      <line x1={12 + rectWidth + 10} y1="43" x2={centerBreakStart - 13} y2="43" stroke={HUD_COLOR} strokeWidth="1" />
      
      {/* Diagonal chevron down */}
      <line x1={centerBreakStart - 13} y1="43" x2={centerBreakStart} y2="56" stroke={HUD_COLOR} strokeWidth="1" />
      {/* Diagonal chevron up */}
      <line x1={centerBreakEnd} y1="56" x2={centerBreakEnd + 13} y2="43" stroke={HUD_COLOR} strokeWidth="1" />
      
      {/* Left corner circle with crosshair */}
      <g className="hud-corner-circle">
        <circle cx="12" cy="18" r="5" fill="none" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="20" y1="18" x2="24" y2="18" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="0" y1="18" x2="4" y2="18" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="12" y1="6" x2="12" y2="10" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1="12" y1="26" x2="12" y2="30" stroke={HUD_COLOR} strokeWidth="1" />
      </g>

      {/* Center break with bars */}
      <rect x={width / 2 - chevronWidth} y="56" width={chevronWidth * 2} height="1" fill={HUD_COLOR} />

      {/* Center FILLED bar */}
      <rect x={width / 2 - barWidth / 2} y="50" width={barWidth} height={barHeight} fill={HUD_COLOR} />
      
      <line x1={centerBreakEnd + 13} y1="43" x2={width - 12 - rectWidth - 10} y2="43" stroke={HUD_COLOR} strokeWidth="1" />
      <rect x={width - 12 - rectWidth} y="38" width={rectWidth} height="9" fill="none" stroke={HUD_COLOR} strokeWidth="1" />

      {/* Right corner circle with crosshair */}
      <g className="hud-corner-circle">
        <circle cx={width - 12} cy="18" r="5" fill="none" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1={width - 20} y1="18" x2={width - 24} y2="18" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1={width} y1="18" x2={width - 4} y2="18" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1={width - 12} y1="6" x2={width - 12} y2="10" stroke={HUD_COLOR} strokeWidth="1" />
        <line x1={width - 12} y1="26" x2={width - 12} y2="30" stroke={HUD_COLOR} strokeWidth="1" />
      </g>

      {/* Text labels moved to fixed overlay — see HUDLogOverlay */}
    </svg>
  )
}

function ArtemisButton({ onArtemisClick }) {
  return (
    <button
      className="artemis-launch-button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        playSound('PlotCourse')
        if (onArtemisClick) {
          onArtemisClick()
        }
      }}
    >
      <img src="/images/artemis-color.svg" alt="Launch Artemis Mission" />
    </button>
  )
}

function HUDLeftBar({ height, showButton, showJoinMission, gap = 30 }) {
  const verticalCenter = height / 2
  const rectHalf = gap - 10
  const deviceInfo = getDeviceInfo()
  const isMobileLandscape = deviceInfo.isPhone && deviceInfo.isLandscape

  return (
    <svg className="hud-left-bar" width="25" height={height} viewBox={`0 0 25 ${height}`} preserveAspectRatio="none">
      {/* Vertical line */}
      <line x1="12" y1="62" x2="12" y2={verticalCenter - gap} stroke={HUD_COLOR} strokeWidth="0.5" />
      <line x1="12" y1={verticalCenter + gap} x2="12" y2={height - 60} stroke={HUD_COLOR} strokeWidth="0.5" />
      
      {/* Vertical bar indicator - show when button is NOT present OR when window is open OR ALWAYS on mobile landscape */}
      {(!showButton || showJoinMission || isMobileLandscape) && (
        <rect
          className="hud-side-indicator"
          x="8"
          y={verticalCenter - rectHalf}
          width="8"
          height={rectHalf * 2}
          fill={HUD_COLOR}
          stroke={HUD_COLOR}
          strokeWidth="0.5"
          rx="0.5"
        />
      )}
    </svg>
  )
}

function HUDRightBar({ width, height, showButton, showJoinMission, gap = 30 }) {
  const verticalCenter = height / 2
  const rectHalf = gap - 10
  const deviceInfo = getDeviceInfo()
  const isMobileLandscape = deviceInfo.isPhone && deviceInfo.isLandscape

  return (
    <svg className="hud-right-bar" width="25" height={height} viewBox={`0 0 25 ${height}`} preserveAspectRatio="none">
      {/* Vertical line */}
      <line x1="13" y1="62" x2="13" y2={verticalCenter - gap} stroke={HUD_COLOR} strokeWidth="0.5" />
      <line x1="13" y1={verticalCenter + gap} x2="13" y2={height - 60} stroke={HUD_COLOR} strokeWidth="0.5" />
      
      {/* Vertical bar indicator - show when button is NOT present OR when window is open OR ALWAYS on mobile landscape */}
      {(!showButton || showJoinMission || isMobileLandscape) && (
        <rect
          className="hud-side-indicator"
          x="9"
          y={verticalCenter - rectHalf}
          width="8"
          height={rectHalf * 2}
          fill={HUD_COLOR}
          stroke={HUD_COLOR}
          strokeWidth="0.5"
          rx="0.5"
        />
      )}
    </svg>
  )
}

export default HUDFrame
